// UC30 — storage sites. The Administrator of a Registrar sees its own; the
// System Administrator sees every Registrar's in one cumulated list, with a
// Registrar column and filter (UC21-09).
// per docs/shape-uc-30-sites.md §1, §4, §5
// per docs/shape-uc-31-credential-history.md §Approach (the site half)
// per docs/shape-uc-30-sites.md §7 (the criteria are answered by the server)
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudIcon, MudSelect, MudTable } from '@egov-moldova/mud-react';
import {
  type ActiveFilter,
  EmptyRegisterMessage,
  FilterBar,
  formatDateTime,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { useSession } from '../../hooks/use-session';
import { useSessionRegistrarName } from '../../hooks/use-session-registrar-name';
import { HistoryModal } from '../../features/history/HistoryModal';
import { BlockTags, SITE_BLOCK_LABELS } from '../../features/blocking/BlockTags';
import { blockStates, blockViewerOf, ownBlocked } from '../../features/blocking/block-status';
import { listRegistrars } from '../../features/registrars/api';
import { SearchSelect } from './SearchSelect';
import {
  fetchLocalities,
  fetchSiteRegister,
  filterAreas,
  localityLabel,
  type LocalityDto,
  type SiteCriteria,
  type SiteRow,
} from './sites-api';

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  width?: string;
}

// The search box asks the server, so it waits for the typing to settle.
const SEARCH_DEBOUNCE_MS = 250;

// The address keeps the wireframe's words for the status; the API takes the flag.
function activeCriterion(statut: string): SiteCriteria['active'] {
  if (statut === 'activ') return 'true';
  if (statut === 'inactiv') return 'false';
  return '';
}

export function SitesPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { role = 'registrar-admin' } = useParams<{ role: string }>();
  const activeRole = useSession().data?.activeRole;
  const isSystemAdmin = activeRole === 'SYSTEM_ADMIN';
  // CF-48: the row's switch sets and lifts the viewer's own block only.
  const viewer = blockViewerOf(activeRole);
  const toast = useToast();
  const registrarName = useSessionRegistrarName();
  // caiet UC30-07 read back on the row, for every viewer, the way the wireframe's
  // AdminLocatii does. GET /admin/history serves a Registrar Administrator its
  // own Registrar's sites (docs/shape-uc-30-sites.md §10).
  const [historySite, setHistorySite] = useState<SiteRow | null>(null);

  // ACCS-11/12/13 — the criteria live in the address, so the list is
  // bookmarkable and "Renunță" on the editor returns to the list it left.
  const [params, setParams] = useCriteriaParams();
  const q = params.get('q') ?? '';
  const registrar = params.get('registrar') ?? '';
  const geo = params.get('geo') ?? '';
  const statut = params.get('statut') ?? '';

  function setCriteria(patch: Record<string, string>): void {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next);
  }

  // UC30-02: every criterion is answered by the server, under the Registrar
  // scope the policy fixes. The rows on screen stay while the next answer is in
  // flight, so a keystroke does not blank the table.
  const searched = useDebouncedValue(q.trim(), SEARCH_DEBOUNCE_MS);
  const asked: SiteCriteria = { q: searched, geographyId: geo, active: activeCriterion(statut) };
  const sites = useQuery({
    queryKey: ['admin-sites', registrar, 'register', asked],
    queryFn: () => fetchSiteRegister(registrar, asked),
    placeholderData: keepPreviousData,
  });
  const localities = useQuery({
    queryKey: ['admin-site-localities'],
    queryFn: fetchLocalities,
    staleTime: 5 * 60_000,
  });
  const registrars = useQuery({
    queryKey: ['admin-registrars', ''],
    queryFn: () => listRegistrars({}),
    enabled: isSystemAdmin,
    staleTime: 5 * 60_000,
  });

  const localityById = useMemo(() => {
    const index = new Map<string, LocalityDto>();
    for (const locality of localities.data ?? []) index.set(locality.id, locality);
    return index;
  }, [localities.data]);

  const update = useMutation({
    mutationFn: ({ site, active }: { site: SiteRow; active: boolean }) =>
      apiClient.patch(
        `/admin/sites/${encodeURIComponent(site.internalCode)}`,
        { active },
        {
          params: isSystemAdmin ? { registrarId: site.registrarId } : {},
        },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-sites'] }),
    // SITE_BLOCKED_BY_* when the other level blocked the site in the meantime.
    onError: (err: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-sites'] });
      toast({
        variant: 'error',
        title: t('sites.toast.status_failed'),
        text: t(apiErrorKey(err, 'api.error.unknown')),
        closeLabel: t('common.close'),
      });
    },
  });

  const rows = useMemo(() => sites.data?.sites ?? [], [sites.data]);
  const scopeTotal = sites.data?.scopeTotal ?? 0;
  const scopeGeographyIds = sites.data?.scopeGeographyIds;

  // The Localitate filter offers the places that actually occur in the scope,
  // the way the wireframe does (a nomenclator of 1 700 entries as a filter would
  // be mostly dead options), and the areas above them, so a raion or a
  // municipality narrows to every site under it. Taken from the scope before the
  // criteria, so choosing one does not leave it the only option.
  const localityOptions = useMemo(
    () =>
      filterAreas(scopeGeographyIds ?? [], localityById)
        .sort((a, b) => a.name.localeCompare(b.name, 'ro'))
        .map((locality) => ({ value: locality.id, label: localityLabel(locality) })),
    [scopeGeographyIds, localityById],
  );

  const registrarOptions = useMemo(
    () =>
      (registrars.data ?? []).map((entity) => ({
        value: entity.idno,
        label: entity.name,
        meta: `IDNO ${entity.idno}`,
      })),
    [registrars.data],
  );

  const listPath = `/${role}/sites`;
  const criteria = params.toString();
  const suffix = criteria ? `?${criteria}` : '';

  // The editor is addressed by the site's MPass code, as every write addresses
  // it (docs/shape-uc-30-sites.md §9).
  function editPath(site: SiteRow): string {
    const forEditor = new URLSearchParams(params);
    if (isSystemAdmin) forEditor.set('registrar', site.registrarId);
    return `${listPath}/${encodeURIComponent(site.internalCode)}/edit?${forEditor.toString()}`;
  }

  // The viewer's own block: lifting it is never confirmed, setting it is. A block
  // of the other level is not the viewer's to lift and is not offered.
  async function toggleActive(site: SiteRow): Promise<void> {
    const lifting = ownBlocked(site, viewer);
    if (!lifting) {
      const ok = await confirm({
        title: t('sites.confirm.deactivate.title'),
        body: t('confirm.site.body', { name: site.name }),
        confirmLabel: t('sites.action.deactivate'),
        destructive: true,
      });
      if (!ok) return;
    }
    update.mutate({ site, active: lifting });
  }

  const filtered = rows.length !== scopeTotal;

  // The search term gets no chip: it clears from the box's own ×, which is the
  // wireframe's own rule for the shared bar (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(registrar
      ? [
          {
            key: 'registrar',
            label: t('sites.column.registrar'),
            value: registrarOptions.find((o) => o.value === registrar)?.label ?? registrar,
            onRemove: () => setCriteria({ registrar: '' }),
          },
        ]
      : []),
    ...(geo
      ? [
          {
            key: 'geo',
            label: t('sites.column.locality'),
            value: localityOptions.find((o) => o.value === geo)?.label ?? geo,
            onRemove: () => setCriteria({ geo: '' }),
          },
        ]
      : []),
    ...(statut
      ? [
          {
            key: 'statut',
            label: t('sites.column.status'),
            value: t(statut === 'activ' ? 'sites.filter.active' : 'sites.filter.inactive'),
            onRemove: () => setCriteria({ statut: '' }),
          },
        ]
      : []),
  ];

  // The wireframe leads the row with the contextual menu and has the same
  // columns after it (AdminLocatii.tsx `leading-actions-table`, docs/ui-shape.md
  // §Shared leading row-actions column). The System Administrator's list is
  // seven columns wide and scrolls inside the MUD table's own wrapper, the
  // documented overflow option for tables past six.
  const columns: TableColumn[] = [
    { key: 'actions', label: t('sites.column.actions'), width: '56px' },
    { key: 'code', label: t('sites.column.code') },
    { key: 'name', label: t('sites.column.name') },
    ...(isSystemAdmin ? [{ key: 'registrar', label: t('sites.column.registrar') }] : []),
    { key: 'locality', label: t('sites.column.locality') },
    { key: 'status', label: t('sites.column.status') },
    { key: 'updated', label: t('sites.column.updated') },
  ];
  const tableRows = rows.map((site) => {
    const locality = localityById.get(site.geographyId);
    return {
      id: site.id,
      actions: '',
      code: site.internalCode,
      name: site.name,
      registrar: site.registrarName,
      locality: locality ? localityLabel(locality) : t('sites.locality.missing'),
      status: blockStates(site, viewer)
        .map((state) => t(SITE_BLOCK_LABELS[state]))
        .join(', '),
      updated: formatDateTime(site.updatedAtIso),
    };
  });

  // An empty register keeps its table: the wireframe leaves the head, the frame
  // and the actions column standing and puts the message in one centred cell
  // spanning every column (AdminLocatii.tsx:220). Both QueryState branches render
  // this same node; mud-table draws its `empty` slot once `rows` is [].
  const register = (
    <div className="simsm-sites__table">
      <MudTable
        className="simsm-table"
        columns={columns}
        rows={tableRows}
        rowStyle="zebra"
        disableSort={true}
        aria-label={t('sites.table.aria_label')}
        // The whole row opens the site's editor (AdminLocatii.tsx:207 `tr.clickable`).
        hoverable
        onMudRowClick={onRowOpen((index) => {
          const site = rows[index];
          if (site) navigate(editPath(site));
        })}
      >
        {/* The wireframe's leading actions column has no visible heading
            (AdminLocatii.tsx `<th className="leading-actions-column"
            aria-label="Acțiuni" />`); the slotted, visually hidden text is
            what names it for assistive technology. */}
        <span slot="header-cell-actions" className="visually-hidden">
          {t('sites.column.actions')}
        </span>
        {/* The remaining headers may run to two lines. In Russian
            "Последнее изменение" is 185px on one line — wider than any date
            under it — so the header, not the value, was what pushed the
            column past the frame. The wireframe's `.tbl.compact th` does the
            same, for the same stated reason. */}
        {columns
          .filter((column) => column.key !== 'actions')
          .map((column) => (
            <span
              key={`header-${column.key}`}
              slot={`header-cell-${column.key}`}
              className="simsm-table__th"
            >
              {column.label}
            </span>
          ))}
        <EmptyRegisterMessage>
          {scopeTotal === 0 ? t('sites.empty') : t('sites.empty_filtered')}
        </EmptyRegisterMessage>
        {rows.map((site, index) => {
          const locality = localityById.get(site.geographyId);
          return [
            <span key={`actions-${site.id}`} slot={`cell-actions-${index}`}>
              <RowActionsMenu
                label={t('sites.actions_for', { name: site.name })}
                actions={[
                  { label: t('sites.action.edit'), onSelect: () => navigate(editPath(site)) },
                  ...(isSystemAdmin
                    ? [
                        {
                          label: t('sites.action.open_registrar'),
                          onSelect: () => navigate(`/${role}/registrars/${site.registrarId}`),
                        },
                      ]
                    : []),
                  {
                    label: t('history.action.open'),
                    onSelect: () => setHistorySite(site),
                  },
                  {
                    label: ownBlocked(site, viewer)
                      ? t('sites.action.activate')
                      : t('sites.action.deactivate'),
                    danger: !ownBlocked(site, viewer),
                    disabled: update.isPending,
                    onSelect: () => void toggleActive(site),
                  },
                ]}
              />
            </span>,
            // Cod (MPass) is an identifier, and the wireframe leaves it as one:
            // `<td className="num">{l.cod}</td>` (AdminLocatii.tsx:214). An
            // underlined code reads as identifying its destination, and this one
            // opened an editor for the whole row; two columns away sat a second
            // link, identical in dress, that went somewhere else entirely.
            <span key={`code-${site.id}`} slot={`cell-code-${index}`}>
              <code>{site.internalCode}</code>
            </span>,
            // The row's own gesture. The wireframe makes the whole row clickable
            // (`tr.clickable` → `deschideEdit`); mud-table publishes no row
            // activation event, so the target narrows to the row's subject —
            // still inside what the wireframe makes clickable, and where every
            // other register here already puts it.
            <span
              key={`name-${site.id}`}
              slot={`cell-name-${index}`}
              className="simsm-sites__cell-name"
            >
              <RouterLink to={editPath(site)} size="sm" variant="strict" underline="hover">
                {site.name}
              </RouterLink>
              <span className="simsm-sites__meta">
                {site.tip ? t(`sites.tip.${site.tip}`) : null}
                {site.tip && site.adresa ? ' · ' : null}
                {site.adresa}
              </span>
            </span>,
            isSystemAdmin ? (
              <RouterLink
                key={`registrar-${site.id}`}
                slot={`cell-registrar-${index}`}
                to={`/${role}/registrars/${site.registrarId}`}
                size="sm"
                underline="hover"
              >
                {site.registrarName}
              </RouterLink>
            ) : null,
            <span key={`locality-${site.id}`} slot={`cell-locality-${index}`}>
              {locality ? (
                localityLabel(locality)
              ) : (
                <span className="simsm-sites__missing">{t('sites.locality.missing')}</span>
              )}
            </span>,
            <span key={`status-${site.id}`} slot={`cell-status-${index}`}>
              <BlockTags item={site} viewer={viewer} labels={SITE_BLOCK_LABELS} />
            </span>,
            // Slotted for its width. Left to mud-table's `.cell-text` this
            // column had no minimum at all — a percentage `max-width`
            // contributes nothing to intrinsic sizing — so the table was
            // free to squeeze it, and it is what the register cut first:
            // "Ultima modificare" rendered "Ul", 26.08.2026 rendered "26".
            // A date is not shortened and not broken.
            <span
              key={`updated-${site.id}`}
              slot={`cell-updated-${index}`}
              className="simsm-table__keep"
            >
              {formatDateTime(site.updatedAtIso)}
            </span>,
          ];
        })}
      </MudTable>
    </div>
  );

  return (
    <section className="simsm-page simsm-sites">
      <PageHeader
        title={t('sites.title')}
        /* Owner decision D5. AdminLocatii.tsx:182 writes one sub in two arms and names the
           Registrar in the second. The third arm here is the session still in flight: a title
           alone beats a sentence with a hole where the name goes. */
        lede={
          isSystemAdmin
            ? t('sites.lede.system_admin')
            : registrarName
              ? t('sites.lede', { name: registrarName })
              : undefined
        }
        actions={
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => navigate(`${listPath}/new${suffix}`)}
          >
            <MudIcon slot="icon-start" name="plus-large" size={20} />
            {t('sites.action.new')}
          </MudButton>
        }
      />

      <FilterBar
        search={{
          value: q,
          onChange: (value) => setCriteria({ q: value }),
          placeholder: t('sites.search.placeholder'),
          ariaLabel: t('sites.filter.search'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => setParams(new URLSearchParams())}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('sites.filters.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            {isSystemAdmin ? (
              <div className="simsm-filter-bar__field">
                <SearchSelect
                  value={registrar}
                  label={t('sites.column.registrar')}
                  options={[
                    { value: '', label: t('sites.filter.all_registrars') },
                    ...registrarOptions,
                  ]}
                  placeholder={t('sites.filter.all_registrars')}
                  ariaLabel={t('sites.column.registrar')}
                  onPick={(value) => setCriteria({ registrar: value })}
                />
              </div>
            ) : null}
            <div className="simsm-filter-bar__field">
              <SearchSelect
                value={geo}
                label={t('sites.column.locality')}
                options={[
                  { value: '', label: t('sites.filter.all_localities') },
                  ...localityOptions,
                ]}
                placeholder={t('sites.filter.all_localities')}
                ariaLabel={t('sites.column.locality')}
                onPick={(value) => setCriteria({ geo: value })}
              />
            </div>
            <div className="simsm-filter-bar__field">
              <MudSelect
                size="medium"
                label={t('sites.column.status')}
                value={statut}
                onMudChange={(event: CustomEvent<{ value: string }>) =>
                  setCriteria({ statut: event.detail.value })
                }
              >
                <option value="">{t('sites.filter.all_statuses')}</option>
                <option value="activ">{t('sites.filter.active')}</option>
                <option value="inactiv">{t('sites.filter.inactive')}</option>
              </MudSelect>
            </div>
          </>
        }
      />

      <QueryState query={sites} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>

      {historySite ? (
        <HistoryModal
          open
          onClose={() => setHistorySite(null)}
          kind="SITE"
          entityId={historySite.id}
          entityLabel={historySite.name}
        />
      ) : null}

      {rows.length > 0 ? (
        <p className="simsm-sites__counter" aria-live="polite">
          {rows.length === 1
            ? t('sites.count_one')
            : t('sites.count_other', { count: rows.length })}
          {filtered ? ` ${t('sites.count_of', { total: scopeTotal })}` : ''}
        </p>
      ) : null}
    </section>
  );
}
